"use client";
import { UseMutationResult, useMutation } from "@tanstack/react-query";
import fetchData from "libs/fetch"; // Adjust relative path based on file location
import { POST_LORA_FLUX_PLG_TXT2IMG } from "api";

// Define interface for LoRA weights
interface Lora {
  path: string;
  scale?: number;
}

// Define request interface
interface GenerateImageRequest {
  prompt: string;
  image_size: string;
  num_inference_steps: number;
  guidance_scale: number;
  num_images: number;
  output_format: string;
  enable_safety_checker: boolean;
  loras: Lora[];
}

// Define response interface
interface GenerateImageResponse {
  image_urls: string[]; 
  record_id: string;
}

// Function to call the API
// Function to call the API with retry logic
const fetchGenerateImageWithLora = async (
  requestData: GenerateImageRequest,
  maxRetries: number = 3,
  retryDelay: number = 1000 // Initial delay of 1 second
): Promise<GenerateImageResponse> => {
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      // Call fetchData to handle JWT authentication
      const data = await fetchData(POST_LORA_FLUX_PLG_TXT2IMG, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });
      return data as GenerateImageResponse;
    } catch (error) {
      attempt++;
      console.error(`Fetch attempt ${attempt} failed:`, error);

      if (attempt > maxRetries) {
        throw error; // Exceeded retries, throw the error
      }

      // Wait before retrying (exponential backoff)
      await new Promise((resolve) => setTimeout(resolve, retryDelay * 2 ** (attempt - 1)));
    }
  }
  throw new Error("Max retries reached without success");
};


// Custom hook for using the LoRA image generation API
// Adjust the hook's return type and specify the mutation types explicitly
export const useFalForLoraTxtToImg = (): UseMutationResult<
  GenerateImageResponse,
  Error,
  { prompt: string; loras: Lora[], image_size: string, num_images: number }
> => {
  const fixedSettings = {
    num_inference_steps: 20,
    guidance_scale: 3.5,
    output_format: "jpeg",
    enable_safety_checker: false,
  };

  return useMutation<GenerateImageResponse, Error, { prompt: string; loras: Lora[]; image_size: string, num_images: number }>({
    mutationFn: async ({ prompt, loras, image_size, num_images }) =>
      fetchGenerateImageWithLora({
        prompt,
        image_size,
        num_images,
        ...fixedSettings,
        loras,
      }),
  });
};


export default useFalForLoraTxtToImg;
